import { useCallback, useEffect, useRef, useState } from 'react';
import { ArrowLeft, ArrowRight, Pause, Play, Plus, RefreshCw, Save, ScanText } from 'lucide-react';
import CharacterFilmConnections from './CharacterFilmConnections';
import CharacterFilmTabs from './CharacterFilmTabs';
import ScreenplayReferences from './ScreenplayReferences';
import ScreenplayReview from './ScreenplayReview';
import FlowkitDashboardTab from './FlowkitDashboardTab';
import { parseFlowProjectId } from '../services/films';
import { useStore } from '../services/store';
import {
  screenplaysApi, screenplayBytes, screenplayCreationAttempt, screenplayEditable, screenplayError, pasteScreenplayDraft,
  screenplayRecoveryKey, serializeScreenplayCreation, restoreScreenplayCreation, screenplayReadError,
  validateScreenplayInput, validateScreenplayRegeneration, screenplayRegenerationReason,
  type ScreenplayCreationAttempt, type ScreenplayInput, type ScreenplayPolicy, type ScreenplayRegenerationSelection,
  screenplayActionReason, screenplayOperation, screenplayOperationObserved, screenplayShouldPoll,
  type ScreenplayAction, type ScreenplayOperation, type ScreenplayRecord, type ScreenplaySelection, type ScreenplaySummary,
} from '../services/screenplays';
import './ScreenplayFilmPanel.css';

const SELECTED_KEY = 'studio-screenplay-selected';
export const DEFAULT_PRODUCT_FOLDER = '/home/ai/homelab/DDlab.cloud/data/films/products';
export const DEFAULT_CHARACTER_FOLDER = '/home/ai/homelab/DDlab.cloud/data/films/characters';
type ScreenplayFormInput = Required<Omit<ScreenplayInput, 'directionProfile'>> & Pick<ScreenplayInput, 'directionProfile'>;
const EMPTY_INPUT: ScreenplayFormInput = {
  title: '',
  draft: '',
  productFolder: DEFAULT_PRODUCT_FOLDER,
  characterFolder: DEFAULT_CHARACTER_FOLDER,
  material: 'realistic',
  planner: 'claude',
};
const RECOVERY_UNAVAILABLE = 'Local recovery unavailable. Keep this tab open until the screenplay is confirmed saved.';
let retainedCreation: { key: string | null; attempt: ScreenplayCreationAttempt; warning: string } | undefined;
function currentRecoveryKey() { return screenplayRecoveryKey(useStore.getState().user?.username); }
function storedCreation(key: string | null) {
  if (!key) return;
  if (retainedCreation?.key === key) return retainedCreation;
  try {
    const attempt = restoreScreenplayCreation(sessionStorage.getItem(key));
    if (attempt) return retainedCreation = { key, attempt, warning: '' };
  } catch {}
}
function rememberCreation(key: string | null, attempt: ScreenplayCreationAttempt) {
  let warning = RECOVERY_UNAVAILABLE;
  if (key && key === currentRecoveryKey()) {
    try {
      const serialized = serializeScreenplayCreation(attempt);
      sessionStorage.setItem(key, serialized);
      if (sessionStorage.getItem(key) === serialized) warning = '';
    } catch {}
  }
  return retainedCreation = { key, attempt, warning };
}
const operationMemory = new Map<string, ScreenplayOperation>();
function rememberOperation(id: string, operation?: ScreenplayOperation) {
  if (operation) operationMemory.set(id, operation); else operationMemory.delete(id);
  try {
    const key = `studio-screenplay-operation:${id}`;
    if (operation) sessionStorage.setItem(key, JSON.stringify(operation)); else sessionStorage.removeItem(key);
  } catch {}
}
function storedOperation(id: string | null): ScreenplayOperation | null {
  if (!id) return null;
  if (operationMemory.has(id)) return operationMemory.get(id)!;
  try {
    const value = JSON.parse(sessionStorage.getItem(`studio-screenplay-operation:${id}`) ?? 'null') as ScreenplayOperation | null;
    if (value?.action === 'regenerate') {
      if (!value.regeneration || !value.planHash) return null;
      validateScreenplayRegeneration({ ...value.regeneration, planHash: value.planHash, confirmCredits: true });
    }
    if (value?.id === id && ['analyze', 'prepare', 'approve', 'run', 'regenerate', 'pause', 'resume'].includes(value.action)
      && (value.episodeNumbers === undefined || (Array.isArray(value.episodeNumbers) && value.episodeNumbers.length > 0
        && value.episodeNumbers.length <= 8 && new Set(value.episodeNumbers).size === value.episodeNumbers.length
        && value.episodeNumbers.every(number => Number.isInteger(number) && number >= 1 && number <= 8)))
      && typeof value.hadMatches === 'boolean' && (!value.task || (['analyze', 'prepare', 'run'].includes(value.task.kind)
        && ['queued', 'running', 'paused', 'complete', 'failed'].includes(value.task.state) && typeof value.task.step === 'string')))
      return value;
  } catch {}
  return null;
}
function storedSelection(): string | null {
  try { return parseFlowProjectId(sessionStorage.getItem(SELECTED_KEY) ?? ''); } catch { return null; }
}
function rememberSelection(id: string | null) {
  try { if (id) sessionStorage.setItem(SELECTED_KEY, id); else sessionStorage.removeItem(SELECTED_KEY); } catch {}
}
function inputOf(record: ScreenplayRecord): ScreenplayFormInput {
  return { title: record.title, draft: record.draft, productFolder: record.productFolder,
    characterFolder: record.characterFolder, material: record.material, planner: record.planner ?? 'gemini',
    ...(record.directionProfile ? { directionProfile: record.directionProfile } : {}) };
}

type ScreenplayFilmPanelProps = {
  onBackToUgc?: () => void; onUnsavedChange?: (dirty: boolean) => void;
};
export default function ScreenplayFilmPanel(props: ScreenplayFilmPanelProps) {
  const username = useStore(state => state.user?.username);
  return <ScreenplayWorkspace key={username ?? ''} {...props} ownerKey={screenplayRecoveryKey(username)} />;
}
function ScreenplayWorkspace({ onBackToUgc, onUnsavedChange, ownerKey }: ScreenplayFilmPanelProps & { ownerKey: string | null }) {
  const [recovery] = useState(() => storedCreation(ownerKey));
  const [selectedId, setSelectedId] = useState(() => recovery?.attempt.input.id ?? storedSelection());
  const [record, setRecord] = useState<ScreenplayRecord | null>(null);
  const [films, setFilms] = useState<ScreenplaySummary[]>([]);
  const [policy, setPolicy] = useState<ScreenplayPolicy | null>(null);
  const [policyError, setPolicyError] = useState('');
  const [flowProjectId, setFlowProjectId] = useState('');
  const [flowProjectMode, setFlowProjectMode] = useState<'create' | 'existing'>('create');
  const [episodeNumbers, setEpisodeNumbers] = useState<number[]>([]);
  const requestedFlowProjectId = !record?.execution && flowProjectMode === 'existing' ? flowProjectId : undefined;
  const [input, setInput] = useState<ScreenplayFormInput>(() => recovery
    ? { ...validateScreenplayInput(recovery.attempt.input), planner: recovery.attempt.input.planner ?? 'gemini' } : { ...EMPTY_INPUT });
  const [inputDirty, setInputDirty] = useState(false);
  const [view, setView] = useState('inputs');
  const [choices, setChoices] = useState<ScreenplaySelection[]>([]);
  const [choicesDirty, setChoicesDirty] = useState(false);
  const choicesDirtyRef = useRef(false);
  const [operation, setOperation] = useState(() => storedOperation(selectedId));
  const operationRef = useRef(operation);
  const [attempt, setAttempt] = useState(recovery?.attempt);
  const attemptRef = useRef(attempt);
  const [recoveryWarning, setRecoveryWarning] = useState(recovery?.warning ?? '');
  const [busy, setBusy] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [readError, setReadError] = useState('');
  const [listError, setListError] = useState('');
  const mounted = useRef(false);
  const working = useRef(false);
  const selection = useRef({ id: selectedId, epoch: 0 });
  const readSequence = useRef(0);
  const controllers = useRef(new Set<AbortController>());
  const listController = useRef<AbortController | null>(null);
  const policyController = useRef<AbortController | null>(null);
  const failedReads = useRef(new Set<string>());
  const reconnecting = useRef(false);
  const onlineArmed = useRef(true);
  const readFailure = useCallback((kind: string, failure: unknown) => {
    const detail = screenplayReadError(failure);
    if (detail.code.startsWith('SCREENPLAY_READ_')) {
      failedReads.current.add(kind);
      if (!reconnecting.current) onlineArmed.current = true;
    } else failedReads.current.delete(kind);
    return detail.message;
  }, []);

  const accept = useCallback((value: ScreenplayRecord, id: string, resetInput = false) => {
    if (value.id !== id || value.version !== 2 || !(['title', 'draft', 'productFolder', 'characterFolder', 'material'] as const)
      .every(field => typeof value[field] === 'string')
      || (value.planner !== undefined && !['gemini', 'codex', 'claude'].includes(value.planner))
      || (value.directionProfile !== undefined && value.directionProfile !== 'cinematic-luxury-v1')
      || (value.analysisProvider !== undefined && !['gemini', 'codex', 'claude'].includes(value.analysisProvider))) {
      throw new Error('The saved screenplay does not match the requested film. Refresh before acting.');
    }
    if (!mounted.current || selection.current.id !== id || ownerKey !== currentRecoveryKey()) return;
    const pending = attemptRef.current;
    if (pending?.input.id === id) {
      screenplayCreationAttempt(value, pending);
      try {
        const stored = ownerKey && restoreScreenplayCreation(sessionStorage.getItem(ownerKey));
        if (stored && stored.input.id === id && screenplayCreationAttempt(stored.input, pending)) sessionStorage.removeItem(ownerKey!);
      } catch {}
      if (retainedCreation?.key === ownerKey && retainedCreation.attempt === pending) retainedCreation = undefined;
      attemptRef.current = undefined; setAttempt(undefined); setRecoveryWarning(''); setError('');
    }
    setRecord(value); setReadError(''); failedReads.current.delete('record');
    setEpisodeNumbers(current => (resetInput ? [] : current).filter(number => value.plan?.episodes.some(episode => episode.number === number)
      && !value.outputs?.some(output => output.episode === number)));
    if (resetInput) { setInput(inputOf(value)); setInputDirty(false); }
    if (value.execution?.flowProjectId) setFlowProjectId(value.execution.flowProjectId);
    if (resetInput || !choicesDirtyRef.current) {
      setChoices((value.selections ?? []).map(item => ({ ...item, assetIds: [...item.assetIds] })));
      choicesDirtyRef.current = false; setChoicesDirty(false);
    }
    if (operationRef.current && screenplayOperationObserved(operationRef.current, value)) {
      rememberOperation(id); operationRef.current = null; setOperation(null); setError('');
    }
    setFilms(current => [{ version: 2, id: value.id, title: value.title, status: value.status,
      createdAt: value.createdAt, updatedAt: value.updatedAt, task: value.task, blocker: value.blocker, planner: value.planner ?? 'gemini',
      ...(value.directionProfile ? { directionProfile: value.directionProfile } : {}),
      episodeCount: value.analysis?.episodes.length ?? 0, assetCount: value.assets?.length ?? 0,
      outputCount: value.outputs?.length ?? 0 }, ...current.filter(film => film.id !== id)]);
  }, [ownerKey]);

  const refreshList = useCallback(async () => {
    listController.current?.abort();
    const controller = new AbortController(); listController.current = controller;
    try {
      const result = await screenplaysApi.list(controller.signal);
      if (!Array.isArray(result.films)) throw new Error('The screenplay library could not be read.');
      if (mounted.current && !controller.signal.aborted) { setFilms(result.films); setListError(''); failedReads.current.delete('library'); }
    } catch (failure) { if (mounted.current && !controller.signal.aborted) setListError(readFailure('library', failure)); }
  }, [readFailure]);

  const policyVersion = record?.execution ? record.plan?.audit.policyVersion : undefined;
  const refreshPolicy = useCallback(async () => {
    policyController.current?.abort();
    const controller = new AbortController(); policyController.current = controller; controllers.current.add(controller);
    setPolicy(null);
    try {
      const value = await screenplaysApi.policy(controller.signal, input.directionProfile, policyVersion);
      if (mounted.current && !controller.signal.aborted) { setPolicy(value); setPolicyError(''); failedReads.current.delete('policy'); }
    } catch (failure) { if (mounted.current && !controller.signal.aborted) setPolicyError(readFailure('policy', failure)); }
    finally { controllers.current.delete(controller); }
  }, [readFailure, input.directionProfile, policyVersion]);

  const refreshRecord = useCallback(async (id: string, resetInput = false, signal?: AbortSignal) => {
    const sequence = ++readSequence.current, epoch = selection.current.epoch;
    try {
      const value = await screenplaysApi.get(id, signal);
      if (mounted.current && !signal?.aborted && selection.current.id === id
        && selection.current.epoch === epoch && readSequence.current === sequence) accept(value, id, resetInput);
      return value;
    } catch (failure) {
      if (mounted.current && !signal?.aborted && selection.current.id === id && selection.current.epoch === epoch
        && readSequence.current === sequence) {
        setReadError(readFailure('record', failure));
        const status = (failure as { response?: { status?: number } })?.response?.status;
        if (status === 404 && !attemptRef.current) {
          rememberSelection(null);
        }
      }
      throw failure;
    }
  }, [accept, readFailure]);

  useEffect(() => {
    mounted.current = true;
    void refreshList();
    return () => {
      mounted.current = false; listController.current?.abort();
      controllers.current.forEach(active => active.abort());
    };
  }, [refreshList]);
  useEffect(() => { void refreshPolicy(); }, [refreshPolicy]);

  useEffect(() => {
    if (!selectedId || working.current) return;
    const controller = new AbortController(); controllers.current.add(controller); setLoading(true);
    void refreshRecord(selectedId, true, controller.signal).catch(() => {}).finally(() => {
      if (!controller.signal.aborted) setLoading(false);
    });
    return () => { controller.abort(); controllers.current.delete(controller); };
  }, [selectedId, refreshRecord]);

  useEffect(() => {
    const offline = () => { onlineArmed.current = true; };
    const online = () => {
      if (!mounted.current || working.current || reconnecting.current || !onlineArmed.current || !failedReads.current.size) return;
      onlineArmed.current = false; reconnecting.current = true;
      const controller = new AbortController(); controllers.current.add(controller);
      const id = selection.current.id;
      void Promise.all([refreshList(), refreshPolicy(), id ? refreshRecord(id, !record, controller.signal).catch(() => {}) : undefined])
        .finally(() => { controllers.current.delete(controller); reconnecting.current = false; });
    };
    window.addEventListener('offline', offline); window.addEventListener('online', online);
    return () => { window.removeEventListener('offline', offline); window.removeEventListener('online', online); };
  }, [record, refreshList, refreshPolicy, refreshRecord]);

  const poll = screenplayShouldPoll(record, !!operation);
  useEffect(() => {
    if (!selectedId || !poll) return;
    const controller = new AbortController(); controllers.current.add(controller); let inFlight = false;
    const timer = window.setInterval(() => {
      if (inFlight || working.current) return;
      inFlight = true;
      void refreshRecord(selectedId, false, controller.signal).catch(() => {}).finally(() => { inFlight = false; });
    }, 4000);
    return () => { window.clearInterval(timer); controller.abort(); controllers.current.delete(controller); };
  }, [selectedId, poll, refreshRecord]);

  useEffect(() => { onUnsavedChange?.(inputDirty || choicesDirty || !!attempt || !!busy); }, [inputDirty, choicesDirty, attempt, busy, onUnsavedChange]);
  useEffect(() => {
    if (!inputDirty && !choicesDirty && !attempt && !busy) return;
    const warn = (event: BeforeUnloadEvent) => { event.preventDefault(); event.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [inputDirty, choicesDirty, attempt, busy]);

  const discardAttempt = useCallback(() => {
    if (ownerKey) {
      try { sessionStorage.removeItem(ownerKey); } catch {}
    }
    retainedCreation = undefined;
    attemptRef.current = undefined;
    setAttempt(undefined);
    setRecoveryWarning('');
    setError('');
    setReadError('');
    rememberSelection(null);
    selection.current = { id: null, epoch: selection.current.epoch + 1 };
    readSequence.current++;
    setSelectedId(null);
    setRecord(null);
    setInput({ ...EMPTY_INPUT });
    setInputDirty(false);
    setView('inputs');
    setChoices([]);
    choicesDirtyRef.current = false;
    setChoicesDirty(false);
    setFlowProjectId('');
    setFlowProjectMode('create');
    setEpisodeNumbers([]);
  }, [ownerKey]);

  function select(id: string | null) {
    if (working.current || attempt || ((inputDirty || choicesDirty) && !window.confirm('Discard unsaved screenplay inputs or image selections?'))) return;
    if (selection.current.id === id) return;
    selection.current = { id, epoch: selection.current.epoch + 1 }; readSequence.current++;
    rememberSelection(id); setSelectedId(id); setRecord(null); setInput({ ...EMPTY_INPUT }); setInputDirty(false);
    setView('inputs'); setChoices([]); choicesDirtyRef.current = false; setChoicesDirty(false);
    setFlowProjectId(''); setFlowProjectMode('create'); setEpisodeNumbers([]);
    const saved = storedOperation(id); operationRef.current = saved; setOperation(saved);
    setError(''); setReadError('');
  }

  function reviseCinematicFilm() {
    if (!record || working.current || operationRef.current || attempt) return;
    const nextInput = { title: record.title, draft: record.draft, productFolder: record.productFolder,
      characterFolder: record.characterFolder, material: record.material, planner: record.planner ?? 'gemini' as const,
      directionProfile: 'cinematic-luxury-v1' as const };
    selection.current = { id: null, epoch: selection.current.epoch + 1 }; readSequence.current++;
    rememberSelection(null); setSelectedId(null); setRecord(null); setInput(nextInput); setInputDirty(true);
    setView('inputs'); setChoices([]); choicesDirtyRef.current = false; setChoicesDirty(false);
    setFlowProjectId(''); setFlowProjectMode('create'); setEpisodeNumbers([]); setError(''); setReadError('');
  }

  async function saveInput() {
    if (working.current || operationRef.current || ownerKey !== currentRecoveryKey() || (record && !screenplayEditable(record))) return;
    let values: ScreenplayInput, intent: ScreenplayCreationAttempt | undefined;
    try {
      values = validateScreenplayInput({ ...input, title: input.title || undefined });
      if (!record) {
        intent = screenplayCreationAttempt(values, attemptRef.current);
        const saved = rememberCreation(ownerKey, intent);
        attemptRef.current = intent; setAttempt(intent); setRecoveryWarning(saved.warning);
      }
    } catch (failure) { setError(screenplayError(failure).message); return; }
    const id = record?.id ?? intent!.input.id, epoch = selection.current.epoch;
    working.current = true; setBusy('Saving screenplay'); setError(''); readSequence.current++;
    const controller = new AbortController(); controllers.current.add(controller);
    if (intent) { rememberSelection(id); selection.current = { id, epoch }; setSelectedId(id); }
    const finish = (value: ScreenplayRecord) => {
      if (!mounted.current || controller.signal.aborted || selection.current.epoch !== epoch) return;
      if (intent) {
        selection.current = { id, epoch }; setSelectedId(id);
      }
      readSequence.current++; accept(value, id, true);
    };
    try {
      finish(intent ? await screenplaysApi.create(intent.input, controller.signal)
        : await screenplaysApi.updateInput(id, values, controller.signal));
    } catch (failure) {
      if (!controller.signal.aborted) {
        if (intent) {
          try { finish(await screenplaysApi.get(id, controller.signal)); }
          catch (readIssue) {
            if (mounted.current && !controller.signal.aborted) {
              setReadError(readFailure('record', readIssue)); setError(screenplayError(failure).message);
            }
          }
        } else if (mounted.current) setError(screenplayError(failure).message);
      }
    } finally {
      controllers.current.delete(controller); working.current = false;
      if (mounted.current) { setBusy(''); void refreshList(); }
    }
  }

  async function queue(action: ScreenplayAction, regeneration?: ScreenplayRegenerationSelection) {
    const reasonFor = (value: ScreenplayRecord) => action === 'regenerate' ? regeneration
      ? screenplayRegenerationReason(value, policyError ? null : policy, regeneration.episodeNumber, regeneration.sceneNumbers)
      : 'Select scenes before regeneration.' : screenplayActionReason(value, action, policy, requestedFlowProjectId, episodeNumbers);
    if (working.current || operationRef.current || !record || inputDirty || choicesDirtyRef.current || readError
      || reasonFor(record)) return;
    working.current = true; setBusy(`${action === 'approve' ? 'Saving approval' : 'Submitting ' + action}`); setError(''); readSequence.current++;
    const controller = new AbortController(); controllers.current.add(controller);
    let submitted = false;
    try {
      const before = await refreshRecord(record.id, false, controller.signal);
      if (controller.signal.aborted) return;
      const blocked = reasonFor(before);
      if (blocked) throw new Error(blocked);
      if (['approve', 'run', 'regenerate'].includes(action) && before.planHash !== record.planHash) throw new Error('The plan changed. Review the current plan before approval or execution.');
      if (action === 'regenerate') {
        const model = before.execution?.episodes?.find(episode => episode.number === regeneration!.episodeNumber)?.videoModel === 'omni_flash' ? 'Omni Flash' : 'Veo';
        if (!window.confirm(`Regenerate ${regeneration!.sceneNumbers.length} video(s) for Episode ${regeneration!.episodeNumber} using ${model}? This uses provider credits. The approved plan and still images stay unchanged; previous exports are retained.`)) return;
      }
      const pending = screenplayOperation(before, action, action === 'run' && requestedFlowProjectId !== undefined
        ? parseFlowProjectId(requestedFlowProjectId)! : undefined, action === 'run' ? episodeNumbers : undefined, regeneration);
      rememberOperation(record.id, pending); operationRef.current = pending; setOperation(pending); submitted = true;
      const value = action === 'approve' ? await screenplaysApi.approve(record.id, record.planHash!, controller.signal)
        : action === 'run' ? await screenplaysApi.run(record.id, record.planHash!, requestedFlowProjectId, controller.signal, episodeNumbers)
          : action === 'regenerate' ? await screenplaysApi.regenerate(record.id, { ...regeneration!, planHash: record.planHash!, confirmCredits: true }, controller.signal)
            : action === 'prepare' ? await screenplaysApi.prepare(record.id, controller.signal, !!before.plan)
              : await screenplaysApi[action](record.id, controller.signal);
      if (!controller.signal.aborted) {
        if (action === 'regenerate' && !screenplayOperationObserved(pending, value)) throw new Error('Regeneration is not confirmed. Refresh the saved state before another request.');
        readSequence.current++; accept(value, record.id);
        rememberOperation(record.id); operationRef.current = null; setOperation(null);
        if (action === 'approve' || action === 'run' || action === 'regenerate') setView('progress');
      }
    } catch (failure) {
      if (!controller.signal.aborted) {
        const detail = screenplayError(failure); setError(detail.message);
        if (submitted && !detail.uncertain && !!(failure as { response?: unknown })?.response) {
          rememberOperation(record.id); operationRef.current = null; setOperation(null);
        }
        if (submitted) { try { await refreshRecord(record.id, false, controller.signal); } catch {} }
      }
    }
    finally { controllers.current.delete(controller); working.current = false; if (mounted.current) setBusy(''); }
  }

  async function saveChoices() {
    if (working.current || operationRef.current || !record || inputDirty || readError || !screenplayEditable(record)) return;
    working.current = true; setBusy('Saving image selections'); setError(''); readSequence.current++;
    const controller = new AbortController(); controllers.current.add(controller);
    try {
      const value = await screenplaysApi.setSelections(record, choices, controller.signal);
      if (!controller.signal.aborted) {
        choicesDirtyRef.current = false; setChoicesDirty(false); readSequence.current++; accept(value, record.id);
      }
    } catch (failure) { if (!controller.signal.aborted) setError(screenplayError(failure).message); }
    finally { controllers.current.delete(controller); working.current = false; if (mounted.current) setBusy(''); }
  }

  function refreshSelected() {
    if (!selectedId || working.current) return;
    const controller = new AbortController(); controllers.current.add(controller);
    void refreshRecord(selectedId, !record, controller.signal).catch(() => {}).finally(() => controllers.current.delete(controller));
  }
  let validation = '';
  try { validateScreenplayInput({ ...input, title: input.title || undefined }); }
  catch (failure) { validation = screenplayError(failure).message; }
  const locked = !!busy || !!attempt || !!operation || (!!selectedId && (!record || !screenplayEditable(record)));
  const plannerLabel = input.planner === 'codex' ? 'Codex' : input.planner === 'claude' ? 'Claude' : 'Gemini';
  const actionReason = (action: ScreenplayAction) => busy || operation ? 'Current work must be resolved before another operation.'
    : readError ? 'Refresh the saved state before acting.' : inputDirty ? 'Save input changes first.'
      : choicesDirty ? 'Save image selections first.' : !record ? 'Save a screenplay first.'
        : screenplayActionReason(record, action, policyError ? null : policy, requestedFlowProjectId, episodeNumbers);

  return <section className="character-film screenplay-film" aria-label="Screenplay films workspace">
    <header className="character-film-header">
      <h1>Screenplay films</h1>
      {onBackToUgc && <button className="screenplay-icon" aria-label="Back to UGC" title="Back to UGC" onClick={onBackToUgc}><ArrowLeft size={18} /></button>}
    </header>
    <CharacterFilmConnections />
    <div className="character-film-layout">
      <aside className="character-film-sidebar" aria-label="Screenplay library">
        <div className="character-film-section-heading"><h2>Screenplays</h2><div className="screenplay-icon-group">
          <button className="screenplay-icon" type="button" title="Refresh screenplay library" aria-label="Refresh screenplay library"
            onClick={() => { void refreshList(); if (attemptRef.current) refreshSelected(); }}><RefreshCw size={16} /></button>
          <button className="screenplay-icon" type="button" title="New screenplay" aria-label="New screenplay" disabled={!!busy || !!attempt} onClick={() => select(null)}><Plus size={18} /></button>
        </div></div>
        {listError && <p role="alert" className="character-film-error">{listError}</p>}
        {!films.length && <p className="character-film-muted">No saved screenplays</p>}
        <ul className="character-film-library">{films.map(film => <li key={film.id}>
          <button type="button" aria-label={`Select screenplay ${film.title}`} aria-pressed={film.id === selectedId}
            disabled={!!busy || !!attempt} onClick={() => select(film.id)}><strong>{film.title}</strong>
            <span>{film.episodeCount} episodes / {film.outputCount} exports</span><span>{film.status === 'complete'
              && film.outputCount < film.episodeCount ? 'batch complete' : film.status.replace(/_/g, ' ')}</span></button>
        </li>)}</ul>
      </aside>
      <main className="character-film-main">
        <div className="character-film-toolbar"><h2>{record?.title ?? (attempt ? input.title : selectedId ? 'Loading screenplay' : 'New screenplay')}</h2>
          {selectedId && <button className="screenplay-icon" type="button" aria-label="Refresh saved screenplay" title="Refresh saved screenplay"
            disabled={!!busy} onClick={refreshSelected}><RefreshCw size={16} /></button>}</div>
        {record && <p className="character-film-meta"><code>{record.id}</code> / {record.status === 'complete'
          && (record.outputs?.length ?? 0) < (record.plan?.episodes.length ?? 0) ? 'batch complete' : record.status.replace(/_/g, ' ')}</p>}
        <CharacterFilmTabs label="Screenplay workspace" selected={view} onSelect={setView} items={[
          { id: 'inputs', label: 'Inputs' }, { id: 'references', label: 'References', disabled: !record?.analysis },
          { id: 'plan', label: 'Plan', disabled: !record?.plan }, { id: 'progress', label: 'Progress', disabled: !record },
          { id: 'rules', label: 'Rules' }, { id: 'dashboard', label: 'Dashboard', disabled: !parseFlowProjectId(record?.execution?.projectId ?? '') },
        ]} />
        {record?.task && <div className="screenplay-task" role="status" aria-live="polite">
          <div className="screenplay-task-info"><strong>{record.task.kind} / {record.task.state}</strong>
            <code>{record.task.step}</code></div>
          <div className="screenplay-task-actions">
            {['paused', 'failed'].includes(record.task.state) && (
              <button type="button" className="character-film-primary" disabled={!!actionReason('resume')}
                title={actionReason('resume') || 'Resume task'} onClick={() => void queue('resume')}>
                <Play size={14} />Resume
              </button>
            )}
            {['queued', 'running'].includes(record.task.state) && (
              <button type="button" disabled={!!actionReason('pause')}
                title={actionReason('pause') || 'Pause after current work'} onClick={() => void queue('pause')}>
                <Pause size={14} />Pause
              </button>
            )}
          </div>
        </div>}
        {record?.directionProfile && record.execution && record.plan?.audit.policyVersion !== '2026-10-05.cinematic.3' &&
          <div className="character-film-warning">
            <p>This film uses an earlier scene plan. Create a revised draft to prepare detailed 4s scenes with first + end frames.</p>
            <button type="button" disabled={!!busy || !!operation || !!attempt} onClick={reviseCinematicFilm}>Create revised draft</button>
          </div>}
        {record?.blocker && <div className="character-film-error screenplay-task" role="alert"><strong>{record.blocker.code}</strong>
          <span>{screenplayError({ response: { status: 409, data: { error: record.blocker } } }).message}</span></div>}
        {operation && <p className="character-film-warning" role="status">Unconfirmed {operation.action} request. Observing saved state; resubmission is disabled.</p>}
        {busy && <p role="status" aria-live="polite">{busy}</p>}
        {loading && !record && <p role="status">Loading saved screenplay</p>}
        {error && <p className="character-film-error" role="alert">{error}</p>}
        {readError && <p className="character-film-error" role="alert">{readError}</p>}
        {readError && !record && !attempt && (
          <div style={{ margin: '4px 0 10px 0' }}>
            <button type="button" className="character-film-muted-button" style={{ padding: '4px 10px', fontSize: '13px' }} onClick={() => select(null)}>
              Start new screenplay
            </button>
          </div>
        )}
        {attempt && <p role="status">Unconfirmed creation: <code>{attempt.input.id}</code>. Inputs stay locked until this exact screenplay is confirmed.
          Refresh saved screenplay to check, or choose Retry same screenplay to send the original input and UUID.</p>}
        {attempt && recoveryWarning && <p className="character-film-warning" role="alert">{recoveryWarning}</p>}
        {attempt && (
          <div style={{ margin: '4px 0 10px 0' }}>
            <button type="button" className="character-film-muted-button" style={{ padding: '4px 10px', fontSize: '13px' }} onClick={discardAttempt}>
              Discard unconfirmed draft
            </button>
          </div>
        )}
        <form hidden={view !== 'inputs'} noValidate onSubmit={event => { event.preventDefault(); void saveInput(); }} aria-label="Screenplay inputs">
          <div className="screenplay-input-grid">
            {([['title', 'Title (optional)', 200], ['material', 'Material', 64], ['productFolder', 'Product image folder', 1000],
              ['characterFolder', 'Character image folder', 1000]] as const).map(([key, label, maxLength]) => <label key={key}>
              {label}<input aria-label={label} value={input[key]} maxLength={maxLength} readOnly={locked}
                onChange={event => { setInput(current => ({ ...current, [key]: event.target.value })); setInputDirty(true); }} /></label>)}
            <label>Planner<select aria-label="Planner" value={input.planner} disabled={locked}
              title="Draft analysis uses the selected planner with the five Flowkit skills. Save a change before analyzing again."
              onChange={event => {
                const planner = event.target.value;
                if (planner === 'gemini' || planner === 'codex' || planner === 'claude') { setInput(current => ({ ...current, planner })); setInputDirty(true); }
              }}><option value="claude">Claude</option><option value="gemini">Gemini</option><option value="codex">Codex</option></select></label>
            <label>Direction profile<select aria-label="Direction profile" value={input.directionProfile ?? ''} disabled={locked}
              onChange={event => {
                const directionProfile = event.target.value;
                if (directionProfile !== '' && directionProfile !== 'cinematic-luxury-v1') return;
                setInput(current => { const next = { ...current }; delete next.directionProfile;
                  if (directionProfile) next.directionProfile = directionProfile; return next; });
                setInputDirty(true);
              }}><option value="">Classic screenplay</option><option value="cinematic-luxury-v1">Cinematic luxury / Omni Flash</option></select></label>
          </div>
          <label>Full screenplay (30-45 seconds per episode)<textarea aria-label="Full screenplay" rows={16} value={input.draft} readOnly={locked} spellCheck={false}
            onPaste={event => {
              if (locked) return;
              event.preventDefault();
              const field = event.currentTarget, clipboard = event.clipboardData.getData('text/plain');
              const start = field.selectionStart, end = field.selectionEnd;
              setInput(current => ({ ...current, draft: pasteScreenplayDraft(current.draft, clipboard, start, end) }));
              setInputDirty(true);
              const cursor = start + clipboard.replace(/\r\n?/g, '\n').length;
              window.requestAnimationFrame(() => field.setSelectionRange(cursor, cursor));
            }}
            onChange={event => { setInput(current => ({ ...current, draft: event.target.value })); setInputDirty(true); }} /></label>
          <div className="character-film-toolbar"><span className="character-film-muted">{screenplayBytes(input.draft).toLocaleString()} / 102,400 UTF-8 bytes</span>
            {inputDirty && <span role="status">Unsaved inputs</span>}</div>
          <div className="character-film-actions">
            <button type="submit" className="character-film-primary" disabled={!!busy || !!operation || !!validation || (!!record && (!inputDirty || !screenplayEditable(record)))}
              title={validation || undefined}><Save size={16} />{attempt ? 'Retry same screenplay' : 'Save screenplay'}</button>
            {attempt && (
              <button type="button" onClick={discardAttempt} title="Discard this unconfirmed draft and start fresh">
                Discard draft
              </button>
            )}
            <button type="button" disabled={!!actionReason('analyze')}
              title={actionReason('analyze') || `Analyze the saved screenplay with ${plannerLabel}`} onClick={() => void queue('analyze')}><ScanText size={16} />Analyze with {plannerLabel}</button>
            {record?.analysis && !inputDirty && (
              <button type="button" onClick={() => setView('references')}>
                <ArrowRight size={16} />Next: References
              </button>
            )}
          </div>
        </form>
        {view === 'references' && record && <ScreenplayReferences record={record} selections={choices} dirty={choicesDirty}
          disabled={locked || inputDirty || !!readError} prepareReason={actionReason('prepare')}
          resumeReason={actionReason('resume')} pauseReason={actionReason('pause')} runReason={actionReason('run')}
          onChange={value => { choicesDirtyRef.current = true; setChoicesDirty(true); setChoices(value); }}
          onSave={() => void saveChoices()} onPrepare={() => void queue('prepare')}
          onResume={() => void queue('resume')} onPause={() => void queue('pause')}
          onRun={() => void queue('run')} onNavigate={setView} />}
        {(view === 'plan' || view === 'progress' || view === 'rules') && <ScreenplayReview view={view} record={record} policy={policy}
          policyError={policyError} flowProjectId={flowProjectId} flowProjectMode={flowProjectMode} onFlowModeChange={setFlowProjectMode}
          flowLocked={!!busy || !!operation || !!record?.execution || record?.task?.kind === 'run'}
          episodeNumbers={operation?.action === 'run' && operation.episodeNumbers ? operation.episodeNumbers : episodeNumbers}
          episodesLocked={!!busy || !!operation || !!readError || inputDirty || choicesDirty || record?.status === 'attention'
            || (record?.task?.kind === 'run' && record.task.state !== 'complete')}
          onEpisodesChange={setEpisodeNumbers}
          regenerationReason={(number, scenes) => actionReason('regenerate') || (record
            ? screenplayRegenerationReason(record, policyError ? null : policy, number, scenes) : 'Select a saved screenplay.')}
          onRegenerate={(episodeNumber, sceneNumbers) => void queue('regenerate', { episodeNumber, sceneNumbers, requestId: crypto.randomUUID() })}
          reason={actionReason} onAction={action => void queue(action)} onFlowChange={setFlowProjectId} onRefreshPolicy={refreshPolicy}
          onNavigate={setView} />}
        {view === 'dashboard' && record?.execution?.projectId && <div className="character-film-dashboard">
          <FlowkitDashboardTab key={record.execution.projectId} projectId={record.execution.projectId} /></div>}
        {policy && <p className="character-film-meta">Policy {policy.version}</p>}
      </main>
    </div>
  </section>;
}
